import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';

export default function HomePage({ user }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageConfig, setPageConfig] = useState(null);

  // Calculate days since joining (from start of join date to start of today)
  const calculateDaysSinceJoining = (joinDate) => {
    if (!joinDate) return 0;
    
    // Parse the join date and set to start of day (00:00:00)
    const joinDateTime = new Date(joinDate);
    const joinDayStart = new Date(joinDateTime.getFullYear(), joinDateTime.getMonth(), joinDateTime.getDate());
    
    // Get start of today (00:00:00)
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Calculate difference in days
    const diffTime = todayStart - joinDayStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Return at least 1 day (same day registration counts as day 1)
    return Math.max(diffDays + 1, 1);
  };

  useEffect(() => {
    loadAnnouncements();
    loadPageConfig();
  }, [user]);

  const loadPageConfig = async () => {
    try {
      const res = await axios.get(`${API}/page-config/student_home?organization_id=${user.organization_id}`);
      setPageConfig(res.data);
    } catch (error) {
      console.error('Failed to load page config', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to load announcements', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to detect and linkify URLs in text
  const linkifyText = (text) => {
    if (!text) return null;
    
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="home-page">
      <div className="max-w-5xl mx-auto">
        {/* Header with proper spacing to avoid overlap with menu button */}
        <div className="ml-0 md:ml-0">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-2">
            {pageConfig?.welcome_text || '歡迎回來'}，{user.username}！
          </h1>
          <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
            {pageConfig?.subtitle || '準備好開始今天的訓練了嗎？'}
          </p>
        </div>

        {/* Stats Cards */}
        {(pageConfig?.show_stats !== false) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">累積積分</div>
            <div className="text-3xl font-bold text-orange-500" data-testid="user-points">{user.points}</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">加入天數</div>
            <div className="text-3xl font-bold text-teal-500">
              {calculateDaysSinceJoining(user.join_date)}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="text-sm text-gray-600 mb-1">學生ID</div>
            <div className="text-xl font-bold text-blue-500" data-testid="student-id">{user.student_id || 'N/A'}</div>
          </div>
        </div>
        )}

        {/* Announcements */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">最新公告</h2>
          
          {loading ? (
            <div>載入中...</div>
          ) : announcements.length === 0 ? (
            <div className="text-gray-500 text-center py-8">暫無公告</div>
          ) : (
            <div className="space-y-6">
              {announcements.map((announcement) => (
                <div key={announcement.announcement_id} className="border-b pb-6 last:border-0" data-testid="announcement-item">
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 break-words">
                    {announcement.title}
                  </h3>
                  <div className="text-sm md:text-base text-gray-600 mb-4 whitespace-pre-wrap break-words leading-relaxed">
                    {linkifyText(announcement.content)}
                  </div>
                  {announcement.images && announcement.images.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mt-4">
                      {announcement.images.map((img, idx) => (
                        <img 
                          key={idx} 
                          src={img.startsWith('http') ? img : `${window.location.origin}/api/media-files${img.replace('/media', '')}`} 
                          alt="" 
                          className="rounded-lg w-full h-40 md:h-48 object-cover" 
                        />
                      ))}
                    </div>
                  )}
                  <div className="text-xs md:text-sm text-gray-500 mt-3">
                    {new Date(announcement.created_at).toLocaleDateString('zh-TW')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
